import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';
import { useTenantStore } from './stores/tenantStore';
import { useAuthStore } from './stores/authStore';
import { AppShell } from './components/layout/AppShell';
import { AuthGuard } from './components/auth/AuthGuard';
import { Loading } from './components/common/Loading';

// Pages
import { Login } from './pages/auth/Login';
import { AuthCallback } from './pages/auth/Callback';
import { Join } from './pages/auth/Join';
import { Home } from './pages/user/Home';
import { Profile } from './pages/user/Profile';
import { Settings } from './pages/user/Settings';
import { Chat } from './pages/user/Chat';
import { Dashboard } from './pages/coach/Dashboard';
import { Users } from './pages/coach/Users';
import { UserDetail } from './pages/coach/UserDetail';
import { CohortDetail } from './pages/coach/CohortDetail';
import { EditFormPage } from './pages/coach/EditFormPage';
import { Groups } from './pages/coach/Groups';
import { NotFound } from './pages/NotFound';

// Tenant wrapper component
function TenantWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  const { loadConfig, isLoading, error } = useTenantStore();
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    if (tenant) {
      loadConfig(tenant);
      checkAuth(tenant);
    }
  }, [tenant, loadConfig, checkAuth]);

  if (!tenant) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return <Loading fullScreen message="Loading..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
          <p className="mt-4 text-sm text-gray-500">
            Make sure the URL is correct and try again.
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}

// Protected wrapper for authenticated routes
function ProtectedRoutes() {
  const { tenant } = useParams<{ tenant: string }>();

  if (!tenant) return null;

  return (
    <AuthGuard tenant={tenant}>
      <AppShell tenant={tenant} />
    </AuthGuard>
  );
}

// Coach routes wrapper
function CoachRoutes() {
  const { tenant } = useParams<{ tenant: string }>();

  if (!tenant) return null;

  return (
    <AuthGuard tenant={tenant} requireRole={['coach', 'admin']}>
      <Outlet />
    </AuthGuard>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Tenant-scoped routes */}
        <Route path="/:tenant" element={<TenantWrapper />}>
          {/* Public routes */}
          <Route path="login" element={<Login />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="join" element={<JoinWrapper />} />

          {/* Protected user routes */}
          <Route element={<ProtectedRoutes />}>
            <Route index element={<HomeWrapper />} />
            <Route path="profile" element={<ProfileWrapper />} />
            <Route path="settings" element={<SettingsWrapper />} />
            <Route path="chat/:targetId" element={<ChatWrapper />} />

            {/* Coach routes */}
            <Route path="coach" element={<CoachRoutes />}>
              <Route index element={<DashboardWrapper />} />
              <Route path="users" element={<UsersWrapper />} />
              <Route path="users/:id" element={<UserDetailWrapper />} />
              <Route path="cohorts/:id" element={<CohortDetailWrapper />} />
              <Route path="cohorts/:cohortId/form/new" element={<EditFormWrapper />} />
              <Route path="cohorts/:cohortId/form/:signalId" element={<EditFormWrapper />} />
              <Route path="groups" element={<GroupsWrapper />} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Global 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

// Root redirect component
function RootRedirect() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">OnlyCoach</h1>
        <p className="text-gray-600 mb-6">Please access your coaching platform via the tenant URL.</p>
        <p className="text-sm text-gray-500">
          Example: <code className="bg-gray-100 px-2 py-1 rounded">onlycoach-web.pages.dev/giraffa</code>
        </p>
      </div>
    </div>
  );
}

// Wrapper components to pass tenant prop
function HomeWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Home tenant={tenant} /> : null;
}

function ProfileWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Profile tenant={tenant} /> : null;
}

function SettingsWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Settings tenant={tenant} /> : null;
}

function DashboardWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Dashboard tenant={tenant} /> : null;
}

function UsersWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Users tenant={tenant} /> : null;
}

function UserDetailWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <UserDetail tenant={tenant} /> : null;
}

function CohortDetailWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <CohortDetail tenant={tenant} /> : null;
}

function EditFormWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <EditFormPage tenant={tenant} /> : null;
}

function GroupsWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Groups tenant={tenant} /> : null;
}

function JoinWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Join tenant={tenant} /> : null;
}

function ChatWrapper() {
  const { tenant } = useParams<{ tenant: string }>();
  return tenant ? <Chat tenant={tenant} /> : null;
}

export default App;
